/**
 * Descarte de audios casi silenciosos (D-7 de la tanda 2). Whisper alucina
 * texto ("Gracias.") con micrófono vacío (nota de interfaz §6.4), así
 * que un turno bajo el umbral no se envía.
 */

/**
 * RMS por debajo del cual el audio grabado se considera silencio.
 *
 * El lab usa 0.01; acá es **0.005** (decisión de la tanda 2, 2026-09-19).
 * Medido contra MVP en Chrome de notebook: silencio 0.0003–0.0005, la voz
 * más baja 0.0167. Con 0.01 la voz quedaba a 1.7× del umbral; con 0.005, el
 * silencio queda 10× por debajo y la voz baja 3× por encima. Para la demo,
 * descartar a alguien que sí habló es peor que enviar un casi-silencio.
 */
export const UMBRAL_RMS_SILENCIO = 0.005;

/** RMS de todas las muestras de todos los canales. `0` si no hay muestras. */
export function calcularRms(canales: readonly Float32Array[]): number {
  let suma = 0;
  let n = 0;
  for (const canal of canales) {
    for (let i = 0; i < canal.length; i++) {
      const v = canal[i] ?? 0;
      suma += v * v;
    }
    n += canal.length;
  }
  return n === 0 ? 0 : Math.sqrt(suma / n);
}

/**
 * Lo que se necesita de un `AudioContext`: solo decodificar. `sampleRate`
 * lo usa la envolvente del habla del avatar (tanda 4, D-4), que decodifica
 * con el mismo contexto.
 */
export interface Decodificador {
  decodeAudioData(data: ArrayBuffer): Promise<{
    numberOfChannels: number;
    sampleRate: number;
    getChannelData(canal: number): Float32Array;
  }>;
}

/**
 * RMS del audio grabado, decodificando **una copia** del binario:
 * `decodeAudioData` desarma el buffer que recibe, y el original es el que
 * se envía. No duplica la captura: mide exactamente lo que se va a enviar.
 */
export async function medirRms(
  decodificador: Decodificador,
  binario: ArrayBuffer,
): Promise<number> {
  const audio = await decodificador.decodeAudioData(binario.slice(0));
  const canales: Float32Array[] = [];
  for (let c = 0; c < audio.numberOfChannels; c++) canales.push(audio.getChannelData(c));
  return calcularRms(canales);
}
